import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

const SESSION_KEY = "pepstep_session_id";

function getSessionId() {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
        id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(SESSION_KEY, id);
    }
    return id;
}

export function CartProvider({ children }) {
    const [items, setItems] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const sessionId = getSessionId();

    // Load cart on mount
    useEffect(() => {
        const load = async () => {
            try {
                const local = localStorage.getItem("pepstep_cart");
                if (local) setItems(JSON.parse(local));
                const res = await axios.get(`${API}/cart/${sessionId}`);
                if (res.data?.items?.length) {
                    setItems(res.data.items);
                }
            } catch (e) {
                // Offline-tolerant: cart still works from localStorage if backend is unreachable.
            }
        };
        load();
    }, [sessionId]);

    // Persist
    useEffect(() => {
        localStorage.setItem("pepstep_cart", JSON.stringify(items));
        const syncRemote = async () => {
            try {
                await axios.put(`${API}/cart`, { session_id: sessionId, items });
            } catch (e) {
                // Offline-tolerant: localStorage is the source of truth until backend is reachable.
            }
        };
        const t = setTimeout(syncRemote, 400);
        return () => clearTimeout(t);
    }, [items, sessionId]);

    const addItem = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.product_id === product.id);
            if (existing) {
                return prev.map((i) =>
                    i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    quantity: qty,
                    image: product.image || "",
                },
            ];
        });
        setIsOpen(true);
    }, []);

    const removeItem = useCallback((productId) => {
        setItems((prev) => prev.filter((i) => i.product_id !== productId));
    }, []);

    const updateQuantity = useCallback((productId, qty) => {
        if (qty <= 0) {
            setItems((prev) => prev.filter((i) => i.product_id !== productId));
            return;
        }
        setItems((prev) =>
            prev.map((i) => (i.product_id === productId ? { ...i, quantity: qty } : i))
        );
    }, []);

    const clear = useCallback(() => {
        setItems([]);
    }, []);

    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const totalQty = items.reduce((s, i) => s + i.quantity, 0);

    return (
        <CartContext.Provider
            value={{
                items,
                addItem,
                removeItem,
                updateQuantity,
                clear,
                subtotal,
                totalQty,
                isOpen,
                setIsOpen,
                sessionId,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
